import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router'

import { session } from '@/auth'
import { useMediaQuery } from '@/lib/use-media'
import { CommandPalette, isPaletteShortcut } from './command-palette'
import { MobileNav } from './mobile-nav'
import { Sidebar } from './sidebar'
import { Topbar } from './topbar'
import { WorkspaceBanners } from './workspace-banners'
import { UpgradePromptDialog } from '@/features/billing/upgrade-prompt'

/** Re-read capabilities every 10 min while visible — there is no /auth/me and roles can change server-side. */
const CAPABILITIES_REFRESH_MS = 10 * 60_000

/**
 * The authenticated frame (FRONTEND_PLAN §6.4): sidebar ≥1024, icon rail 768–1023, off-canvas
 * below. Content column scrolls; the sidebar is sticky.
 */
export function AppShell() {
  const [navOpen, setNavOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const compact = useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
  const { pathname } = useLocation()

  useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  // Cmd/Ctrl-K anywhere in the shell (docs/keyboard-shortcuts.md).
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (isPaletteShortcut(event)) {
        event.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  useEffect(() => {
    const tick = (): void => {
      if (document.visibilityState === 'visible')
        void session.loadCapabilities().catch(() => undefined)
    }
    const timer = setInterval(tick, CAPABILITIES_REFRESH_MS)
    return () => {
      clearInterval(timer)
    }
  }, [])

  return (
    <div className="flex min-h-screen bg-paper">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Sidebar compact={compact} />
      <MobileNav open={navOpen} onOpenChange={setNavOpen} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          onOpenNav={() => {
            setNavOpen(true)
          }}
          onOpenPalette={() => {
            setPaletteOpen(true)
          }}
        />
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        <main id="main" className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-5 sm:px-6 sm:py-6">
          <WorkspaceBanners />
          <Outlet />
        </main>
        <UpgradePromptDialog />
      </div>
    </div>
  )
}
